import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { readAppLanguage } from './i18n/languageStore';
import { USER_CHANGED_EVENT } from './permissions';
import { useTranslation } from './i18n/LanguageContext';
import {
  applyFontPreferences,
  FONT_CHANGED_EVENT,
  readFontPreference,
  writeFontPreference,
  resolveFontScales,
  type FontPreference,
  type FontSizePreset,
} from './fontSettings';

type FontContextValue = {
  fontPreference: FontPreference;
  fontScale: number;
  fontPreset: FontSizePreset | null;
  setFontPreset: (preset: FontSizePreset) => void;
  setCustomScale: (scale: number) => void;
};

const FontContext = createContext<FontContextValue | null>(null);

export function FontProvider({ children }: { children: ReactNode }) {
  const { language } = useTranslation();
  const [fontPreference, setFontPreferenceState] = useState<FontPreference>(() => readFontPreference());

  useEffect(() => {
    const sync = () => setFontPreferenceState(readFontPreference());
    window.addEventListener(FONT_CHANGED_EVENT, sync);
    window.addEventListener(USER_CHANGED_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(FONT_CHANGED_EVENT, sync);
      window.removeEventListener(USER_CHANGED_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  useEffect(() => {
    applyFontPreferences(fontPreference, language);
  }, [fontPreference, language]);

  const setFontPreset = useCallback((preset: FontSizePreset) => {
    const pref: FontPreference = { mode: 'preset', preset };
    writeFontPreference(pref);
    setFontPreferenceState(pref);
    applyFontPreferences(pref, readAppLanguage());
  }, []);

  const setCustomScale = useCallback((scale: number) => {
    const pref: FontPreference = { mode: 'custom', scale };
    writeFontPreference(pref);
    setFontPreferenceState(pref);
    applyFontPreferences(pref, readAppLanguage());
  }, []);

  const { scale } = resolveFontScales(fontPreference);
  const fontPreset = fontPreference.mode === 'preset' ? fontPreference.preset : null;

  const value = useMemo(
    () => ({ fontPreference, fontScale: scale, fontPreset, setFontPreset, setCustomScale }),
    [fontPreference, scale, fontPreset, setFontPreset, setCustomScale]
  );

  return <FontContext.Provider value={value}>{children}</FontContext.Provider>;
}

export function useFontSettings() {
  const ctx = useContext(FontContext);
  if (!ctx) {
    const pref = readFontPreference();
    const { scale } = resolveFontScales(pref);
    return {
      fontPreference: pref,
      fontScale: scale,
      fontPreset: pref.mode === 'preset' ? pref.preset : null,
      setFontPreset: (_preset: FontSizePreset) => {},
      setCustomScale: (_scale: number) => {},
    };
  }
  return ctx;
}
